export type Theme = typeof theme;

export const theme = {
    colors: {
        blue: '#0B66E4',
        lightBlue: '#2a9df4',
        lightestBlue: '#89c5f1',
        green: '#4CAF50',
        lightGreen: '#8BC34A',
        lightestGreen: '#c8e6c9',
        gray: '#9E9E9E',
        grayWithOpacity: 'rgba(158,158,158,0.5)',
        lightGray: '#e0e0e0',
        lightestGray: '#eeeeee',
        red: '#F44336',
        brown: '#795548',
        lightAmber: '#F2E0C8',
        topBar: '#F5F5F5',

        white: '#FFFFFF',
        darkerWhite: '#FBFBFB',
        black: '#000000',
        scrollBar: '#A4A4A4',
        darkGray: '#979797',
        borderColor: '#E9ECEF',
        darkFontColor: '#212529',
        lightBorderColor: '#BECAD6',
        inputTextColor: '#495057',
        lightFontColor: '#5a6169',
        // BELOW COLORS ARE USED TO MODIFY GOOGLE CHARTS
        gridline: '#8d9aa9',
        bar: '#0679f6',
        chartLabel: '#a4a4a4',
    },
    fontWeight: {
        normal: 400,
        bold: 700,
    },
    fontFamily: {
        atlassian: 'system-ui',
        lato: 'Lato, system-ui',
    },
    fontSize: {
        tiny: '9px',
        smallest: '10px',
        tablecell: '11px',
        smaller: '12px',
        small: '14px',
        normal: '16px',
        big: '18px',
    },
    lineHeight: {
        smallest: '9px',
        smaller: '11px',
        small: '17px',
        normal: '24px',
    },
    letterSpacing: {
        sliderLabel: '-0.09px',
        input: '-0.28px',
        small: '0',
        normal: '0.15px',
    },
    shadows: {
        panel: '0px 6px 14px 8px rgba(0, 0, 0, 0.07)',
        pane: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
        rail: 'inset 0 1px 2px 0 rgba(90,97,105,0.1)',
        thumb: '0 2px 3px 0 rgba(90,97,105,0.2),'
            + '0 3px 15px 0 rgba(90,97,105,0.1)',
        hover: '0 3px 6px rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23)',
        slider: '0 0 1px 0 rgba(0,0,0,0.5)',
        menu: '0 2px 4px 0 rgba(0,0,0,0.1)',
        tooltip: '0px 0px 14px rgba(0,0,0,0.29)',
        tab: '5px 0 5px 0 rgba(0, 0, 0, 0.07)',
    },
    zIndex: {
        topBar: 2,
        diagram: 1,
        panel: 6,
        contextMenu: 50,
        modal: 100,
        tab: 5,
        activeTab: 7,
    },
    height: {
        topBar: 60,
        colorPicker: 293,
        timelineChart: '70vh',
        contextMenuOption: 24,
    },
    width: {
        colorPicker: 220,
        tab: 50,
    },
};

export const getFromTheme = (namespace: keyof typeof theme) => (key: string) =>
    ({ theme: providedTheme }: { theme: Theme }) =>
        providedTheme[namespace][key];

export const getColor = (key: keyof typeof theme.colors) =>
    getFromTheme('colors')(key);

export const getFontFamily = (key: keyof typeof theme.fontFamily) =>
    getFromTheme('fontFamily')(key);

export const getFontWeight = (key: keyof typeof theme.fontWeight) =>
    getFromTheme('fontWeight')(key);

export const getFontSize = (key: keyof typeof theme.fontSize) =>
    getFromTheme('fontSize')(key);

export const getLineHeight = (key: keyof typeof theme.lineHeight) =>
    getFromTheme('lineHeight')(key);

export const getLetterSpacing = (key: keyof typeof theme.letterSpacing) =>
    getFromTheme('letterSpacing')(key);

export const getShadow = (key: keyof typeof theme.shadows) =>
    getFromTheme('shadows')(key);

export const getZIndex = (key: keyof typeof theme.zIndex) =>
    getFromTheme('zIndex')(key);

export const getHeight = (key: keyof typeof theme.height) =>
    getFromTheme('height')(key);

export const getWidth = (key: keyof typeof theme.width) =>
    getFromTheme('width')(key);
